/**
 * SelectionProvider - Layer 4: Interaction (Selection System)
 *
 * Manages selection state and gesture handling for molecular visualization.
 * Integrates with MolecularWorkspace for connected element selection.
 *
 * Architecture:
 * - Creates selection state via useSelectionState
 * - Creates GestureManager for declarative gesture handling
 * - Provides workspace reference for flood-fill selection
 * - Exports useSelection hook for consuming components
 *
 * Usage:
 * ```tsx
 * <MolecularSVGProvider workspace={workspace}>
 *   <SelectionProvider>
 *     <SVGCanvas />  // Can use selection state and gestures
 *   </SelectionProvider>
 * </MolecularSVGProvider>
 * ```
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMolecularSVG } from '../contexts/MolecularSVGContext';
import { useSelectionState, type SelectionAPI } from './useSelectionState';
import { GestureManager, type GestureManagerConfig } from '../gestures/GestureManager';

/**
 * Selection context value providing access to selection state and gesture manager
 */
export interface SelectionContextValue extends SelectionAPI {
  /**
   * GestureManager instance for declarative gesture handling
   */
  gestureManager: GestureManager;

  /**
   * Access to workspace for connected element selection
   */
  getWorkspace: () => any; // Using 'any' to avoid circular dependency with molecular-workspace
}

const SelectionContext = createContext<SelectionContextValue | null>(null);

export interface SelectionProviderProps {
  /**
   * Child components that need access to selection state
   */
  children: ReactNode;

  /**
   * Optional initial selection
   */
  initialSelection?: string[];

  /**
   * Controlled selection: when provided, the provider uses this external set
   * instead of internal state. Mutations call onSelectionChange with the next set.
   * Omit for uncontrolled (default) behavior — zero impact on existing usage.
   */
  controlledSelection?: Set<string>;

  /**
   * Optional callback when selection changes
   */
  onSelectionChange?: (selectedIds: Set<string>) => void;

  /**
   * Controlled hover: when provided, the provider uses this external set
   * instead of internal state. Mutations call onHoveredIdsChange with the next set.
   * Omit for uncontrolled (default) behavior — zero impact on existing usage.
   */
  controlledHoveredIds?: Set<string>;

  /**
   * Callback when hover changes in controlled mode
   */
  onHoveredIdsChange?: (hoveredIds: Set<string>) => void;

  /**
   * Optional callback when hover changes (accepts single ID, array of IDs, or null)
   * @deprecated Use onHoveredIdsChange for controlled hover mode
   */
  onHoverChange?: (hoveredIds: string | string[] | null) => void;

  /**
   * Optional GestureManager configuration
   */
  gestureConfig?: GestureManagerConfig;
}

/**
 * SelectionProvider - Manages selection state and gesture handling
 *
 * This provider:
 * 1. Creates selection state via useSelectionState
 * 2. Creates GestureManager for declarative gesture handling
 * 3. Provides workspace reference for flood-fill connected selection
 * 4. Exposes selection API and gesture manager to children
 *
 * @example
 * ```tsx
 * <MolecularSVGProvider workspace={workspace}>
 *   <SelectionProvider
 *     onSelectionChange={(ids) => console.log('Selection:', Array.from(ids))}
 *     gestureConfig={{ debug: true }}
 *   >
 *     <SVGCanvas />
 *   </SelectionProvider>
 * </MolecularSVGProvider>
 * ```
 */
export function SelectionProvider({
  children,
  initialSelection,
  controlledSelection,
  onSelectionChange,
  controlledHoveredIds,
  onHoveredIdsChange,
  onHoverChange,
  gestureConfig,
}: SelectionProviderProps) {
  const { workspace } = useMolecularSVG();

  // Create selection state (supports controlled mode when controlledSelection/controlledHoveredIds are provided)
  const selectionAPI = useSelectionState({
    initialSelection,
    allowMultiSelection: true,
    controlledSelection,
    onSelectionChange,
    controlledHoveredIds,
    onHoveredIdsChange,
    onHoverChange,
  });

  // Create gesture manager (memoized to persist across renders)
  const gestureManager = useMemo(() => {
    return new GestureManager(gestureConfig);
  }, [gestureConfig]);

  // Create context value
  const contextValue = useMemo<SelectionContextValue>(() => ({
    // Selection API
    ...selectionAPI,

    // Gesture manager
    gestureManager,

    // Workspace accessor for connected element selection
    getWorkspace: () => workspace,
  }), [selectionAPI, gestureManager, workspace]);

  return (
    <SelectionContext.Provider value={contextValue}>
      {children}
    </SelectionContext.Provider>
  );
}

/**
 * Hook to access selection context
 *
 * @param options - Optional configuration
 * @param options.optional - If true, returns null instead of throwing when provider is missing
 * @throws Error if used outside SelectionProvider (unless optional: true)
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const selection = useSelection();
 *
 *   const handleClick = (atomId: string) => {
 *     selection.toggleSelection(atomId);
 *   };
 *
 *   return (
 *     <circle
 *       onClick={() => handleClick('atom-1')}
 *       fill={selection.isSelected('atom-1') ? 'blue' : 'gray'}
 *     />
 *   );
 * }
 * ```
 *
 * @example Optional usage
 * ```tsx
 * function OptionalComponent() {
 *   const selection = useSelection({ optional: true });
 *
 *   if (selection) {
 *     // Use selection features
 *   }
 * }
 * ```
 */
export function useSelection(options?: { optional?: boolean }): SelectionContextValue | null {
  const context = useContext(SelectionContext);

  if (!context && !options?.optional) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }

  return context;
}
